import { useState, type ReactNode } from 'react';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';

/**
 * Shared scaffold for settings create/edit dialogs: fixed header + footer
 * around scrollable content, with an unsaved-changes guard — closing while
 * `dirty` first asks for confirmation instead of discarding silently.
 *
 * The discard-warning strings come from the caller so each entity keeps its
 * own translation contexts. The footer render-prop receives `requestClose`
 * so a Cancel button routes through the same guard as the dialog chrome.
 */
export function EditorDialogShell({
  open,
  dirty,
  title,
  discard,
  onClose,
  footer,
  children,
}: {
  open: boolean;
  dirty: boolean;
  title: ReactNode;
  discard: {
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
  };
  onClose: () => void;
  footer: (requestClose: () => void) => ReactNode;
  children: ReactNode;
}) {
  const [discardOpen, setDiscardOpen] = useState(false);

  function requestClose() {
    if (dirty) {
      setDiscardOpen(true);
      return;
    }
    onClose();
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) requestClose();
        }}
      >
        <DialogContent className="flex max-w-lg flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="shrink-0 border-b border-border px-6 py-4 pr-12">
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          {children}
          <DialogFooter className="shrink-0 border-t border-border px-6 py-4 sm:justify-end">
            {footer(requestClose)}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={discardOpen}
        onClose={() => setDiscardOpen(false)}
        onConfirm={() => {
          setDiscardOpen(false);
          onClose();
        }}
        title={discard.title}
        message={discard.message}
        confirmText={discard.confirmText}
        cancelText={discard.cancelText}
        dangerous
      />
    </>
  );
}
